<template>
  <div :class="$style.news_detail">
    <!-- Todo: パンくず実装(phase2) -->
    <div :class="$style.container">
      <div :class="$style.title">{{ newsItem?.title }}</div>
      <div :class="$style.post_date">
        <div :class="$style.date">{{ formatUnixTime(newsItem?.release_date, "YYYY年M月D日") }}</div>
        <!-- Todo: カテゴリ実装(phase2) -->
      </div>
      <PagePartsEditorText
        v-if="newsItem?.lead"
        :text="newsItem.lead"
        :class="$style.lead"
        :before-renewal-style="false"
      />
      <PagePartsEditorText
        v-if="newsItem?.content"
        :text="newsItem.content"
        :class="$style.content"
        :before-renewal-style="false"
      />
    </div>
    <div :class="$style.page_bottom">
      <PagesInterviewShare :class="$style.share" />
      <PagesInterviewNavigationLinks
        :class="$style.navigation"
        :prev-link="nextNewsSlug"
        :next-link="prevNewsSlug"
        list-link="/news"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatUnixTime } from "@tential/ec-gql-schema/utils/functions/date"
import { PublicNewsOneQueryVariables } from "~/types/type.generated"
import { usePublicNewsOneQuery } from "~/gql/urql.generated"
import { createNewsJsonLD, createBreadcrumbJsonLD, useCustomMeta } from "~/composables/meta"
import { useToast } from "~/state"

const route = useRoute()
const customMeta = useCustomMeta()
const toast = useToast()

const newsSlug = typeof route.params.newsSlug === "string" ? route.params.newsSlug : ""
const passQuery = typeof route.query.pass_query === "string" ? route.query.pass_query : ""

const findOneVariables = computed<PublicNewsOneQueryVariables>(() => {
  return {
    filter: {
      slug: newsSlug,
      is_status: passQuery ? undefined : true,
      pass_query: passQuery || undefined,
    },
  }
})

const { data: resNewsOneQuery, error: newsOneError } = await usePublicNewsOneQuery({
  variables: findOneVariables.value,
})

if (!resNewsOneQuery.value?.publicNewsOne) {
  console.warn(resNewsOneQuery.value?.publicNewsOne, "queryResult")
  throw createError({ statusCode: 404, statusMessage: "Page Not Found newsSlug", fatal: process.client })
}

const newsItem = computed(() => {
  return resNewsOneQuery?.value?.publicNewsOne?.news
})
const prevNewsSlug = computed(() => {
  return resNewsOneQuery?.value?.publicNewsOne?.prevSlug
})
const nextNewsSlug = computed(() => {
  return resNewsOneQuery?.value?.publicNewsOne?.nextSlug
})

const meta = computed(() => {
  const title = newsItem.value?.title || "ニュース詳細"
  const description = newsItem.value?.description || newsItem.value?.title || "ニュース詳細説明文"
  const image = newsItem.value?.ogp || newsItem.value?.thumbnail_image || ""
  const url = process.env.BASE_URL + route.fullPath || ""
  const jsonLDInfo = [
    createNewsJsonLD({
      headline: newsItem.value?.title,
      image: newsItem.value?.thumbnail_image,
      description: newsItem.value?.description || newsItem.value?.title,
    }),
    createBreadcrumbJsonLD([
      {
        title: "ニュースページ",
        url: "/news",
      },
      {
        title: newsItem.value?.title || "ニュース詳細",
        url: route.path,
      },
    ]),
  ]
  return {
    title,
    description,
    image,
    url,
    jsonLDInfo,
  }
})

useHead(
  customMeta.fetch({
    ...meta.value,
  }),
)

watch(
  () => newsOneError.value,
  (value) => {
    if (!value) return
    toast?.showErrorToasted(value.message)
  },
)
</script>

<style scoped module lang="scss">
.news_detail {
  @include container-md;
  .container {
    margin-top: 75px;
    @include md {
      margin-top: 175px;
    }
    .title {
      @include main-font;
      font-size: $font-size-18;
      color: $primary-100;
      margin-bottom: 26px;
      line-height: 1.55;
      @include md {
        font-size: $font-size-26;
        margin-bottom: 30px;
      }
    }
    .post_date {
      display: flex;
      padding: 1.25rem 0;
      margin-bottom: 30px;
      font-size: $font-size-11;
      letter-spacing: 0.22px;
      line-height: 1.27;
      font-weight: normal;
      color: $primary-100;
      border-top: 1px solid $primary-20;
      border-bottom: 1px solid $primary-20;
      @include md {
        font-size: $font-size-13;
        line-height: 1.07;
        padding: 22px 0;
        margin-bottom: 50px;
      }
    }

    .lead {
      margin-bottom: 50px;
      @include md {
        margin-bottom: 70px;
      }
    }
    .content {
      margin-bottom: 70px;
      @include md {
        margin-bottom: 110px;
      }
    }
  }
  .page_bottom {
    .share {
      margin-bottom: 50px;
      @include md {
        margin-bottom: 3.75rem;
      }
    }
    .navigation {
      margin-bottom: 80px;
      @include md {
        margin-bottom: 6.25rem;
      }
    }
  }
}
</style>
